import { useState, useEffect } from 'react';
import '../css/sg-platform.css';
import '../css/sg-dashboard.css';
import { Radar } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import L from 'leaflet';
import {
  generatePdfForCulture,
  getPilarBestScores,
} from '../services/fetch_api';

// Add Leaflet imports
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

ChartJS.register(...registerables);

// Leaflet Map Styles

const center = [39.694709639118564, -8.130288197565612]; // [lat, lng]

const customIcon = new L.Icon({
  iconUrl: require('../assets/sustaingrowth.png'), // Luís -> Mudar o icon do Marker
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Função para gerar e abrir o PDF
const handleGeneratePdf = async (certId) => {
  try {
    const pdfUrl = await generatePdfForCulture(certId); // Generate the PDF
    window.open(pdfUrl, '_blank'); // Open the PDF in a new tab
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Failed to generate the PDF. Please try again.');
  }
};

// Component for concluded culture card
const ConcludedCultureCard = ({
  cultura,
  certificado,
  culturaIndex,
  cellValues,
  cellColors,
}) => {
  const [grades, setGrades] = useState({
    ambiental: null,
    social: null,
    economico: null,
    governanca: null,
  });
  const [overallGrade, setOverallGrade] = useState('');

  useEffect(() => {
    const calculateGrades = async () => {
      try {
        // Get the best possible scores for this culture
        const bestScores = await getPilarBestScores(cultura.indicador_adequado);

        // Parse the evaluation details from the certificate
        let avaliacao;
        if (typeof certificado.detalhes_avaliacao === 'string') {
          try {
            const parsedDetails = JSON.parse(certificado.detalhes_avaliacao);
            avaliacao = parsedDetails.pilar_scores || parsedDetails;
          } catch (e) {
            console.error('Error parsing detalhes_avaliacao:', e);
            avaliacao = {
              ambiental: 0,
              social: 0,
              economico: 0,
              governanca: 0,
            };
          }
        } else {
          avaliacao = certificado.detalhes_avaliacao.pilar_scores ||
            certificado.detalhes_avaliacao || {
              ambiental: 0,
              social: 0,
              economico: 0,
              governanca: 0,
            };
        }

        // Calculate percentage for each pillar and determine grade
        const calculateGrade = (score, maxScore) => {
          if (!maxScore || maxScore === 0) return 'D';
          const percentage = (score / maxScore) * 100;
          if (percentage >= 75) return 'A';
          if (percentage >= 50) return 'B';
          if (percentage >= 25) return 'C';
          return 'D';
        };

        const newGrades = {
          ambiental: calculateGrade(
            avaliacao.ambiental,
            bestScores?.ambiental?.total_score || 100
          ),
          social: calculateGrade(
            avaliacao.social,
            bestScores?.social?.total_score || 100
          ),
          economico: calculateGrade(
            avaliacao.economico,
            bestScores?.económico?.total_score || 100
          ),
          governanca: calculateGrade(
            avaliacao.governanca,
            bestScores?.governança?.total_score || 100
          ),
        };

        setGrades(newGrades);

        // Calculate overall grade based on total score percentage
        const totalActualScore =
          avaliacao.ambiental +
          avaliacao.social +
          avaliacao.economico +
          avaliacao.governanca;
        const totalMaxScore =
          (bestScores?.ambiental?.total_score || 100) +
          (bestScores?.social?.total_score || 100) +
          (bestScores?.económico?.total_score || 100) +
          (bestScores?.governança?.total_score || 100);

        const totalPercentage =
          totalMaxScore > 0 ? (totalActualScore / totalMaxScore) * 100 : 0;

        let overallGrade;
        if (totalPercentage >= 75) overallGrade = 'A';
        else if (totalPercentage >= 50) overallGrade = 'B';
        else if (totalPercentage >= 25) overallGrade = 'C';
        else overallGrade = 'D';

        setOverallGrade(overallGrade);
      } catch (error) {
        console.error('Error calculating grades:', error);
        setGrades({
          ambiental: 'D',
          social: 'D',
          economico: 'D',
          governanca: 'D',
        });
        setOverallGrade('D');
      }
    };

    calculateGrades();
  }, [cultura, certificado]);

  const getCellBackground = (rowIdx, colIdx) => {
    if (colIdx === 0) return '#fff'; // Header column

    const pillarNames = ['ambiental', 'social', 'economico', 'governanca'];
    const gradeLetters = ['A', 'B', 'C', 'D'];
    const pillarGrade = grades[pillarNames[rowIdx]];
    const cellGrade = gradeLetters[colIdx - 1];

    if (pillarGrade === cellGrade) {
      return cellColors[cellGrade];
    }

    return '#e5e5e5'; // Default grey color
  };

  return (
    <div className="concluida-details-card">
      <h3>
        Detalhes da Cultura Concluída -{' '}
        {cultura.nome || `Cultura ${culturaIndex + 1}`}
      </h3>
      <div className="concluida-details-content">
        <div className="concluida-details-content-left">
          {cellValues.map((row, rowIdx) =>
            row.map((cell, colIdx) => (
              <div
                className={
                  'concluida-details-content-label' +
                  (colIdx === 0 ? ' bold' : '')
                }
                key={`${rowIdx}-${colIdx}`}
                style={{
                  background: getCellBackground(rowIdx, colIdx),
                }}
              >
                {cell}
              </div>
            ))
          )}
        </div>
        <div className="concluida-details-content-right">
          <h3>{overallGrade}</h3>
          <p>A - de 75 a 100</p>
          <p>B - de 50 a 75</p>
          <p>C - de 25 a 50</p>
          <p>D - de 0 a 25</p>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [explorations, setExplorations] = useState([]);
  const [totalPendingCertifications, setTotalPendingCertifications] =
    useState(0);
  const [totalSubmittedCertifications, setTotalSubmittedCertifications] =
    useState(0);
  const [totalConcludedCertifications, setTotalConcludedCertifications] =
    useState(0);
  const [totalCultures, setTotalCultures] = useState(0);

  const [radarData, setRadarData] = useState({
    labels: ['Ambiental', 'Social', 'Económico', 'Governança'],
    datasets: [],
  });

  // Dropdown state
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [concluidaDropdownOpen, setConcluidaDropdownOpen] = useState({});

  // Helper to always map explorations with title
  const mapExplorationsWithTitles = (explorations) =>
    explorations.map((exploration, idx) => ({
      ...exploration,
      title: exploration.title || exploration.nome || `Exploração ${idx + 1}`,
    }));

  // Carregar explorações SEMPRE que o Dashboard monta (garante atualização ao voltar de outra página)
  useEffect(() => {
    const loadExplorations = () => {
      const storedExplorations =
        JSON.parse(sessionStorage.getItem('exploracoes')) || [];
      const explorationsWithTitles =
        mapExplorationsWithTitles(storedExplorations);
      setExplorations(explorationsWithTitles);

      const pendingCertificationsCount = explorationsWithTitles.reduce(
        (acc, exploration) => {
          return (
            acc +
            (exploration.sg_certificados
              ? exploration.sg_certificados.filter(
                  (certificado) =>
                    certificado.estado === 'CRIADO' ||
                    certificado.estado === 'PENDENTE'
                ).length
              : 0)
          );
        },
        0
      );
      setTotalPendingCertifications(pendingCertificationsCount);
      const submittedCertificationsCount = explorationsWithTitles.reduce(
        (acc, exploration) => {
          return (
            acc +
            (exploration.sg_certificados
              ? exploration.sg_certificados.filter(
                  (certificado) =>
                    certificado.estado === 'PROCESSADO' ||
                    certificado.estado === 'VALIDACAO'
                ).length
              : 0)
          );
        },
        0
      );
      setTotalSubmittedCertifications(submittedCertificationsCount);
      const concludedCertificationsCount = explorationsWithTitles.reduce(
        (acc, exploration) => {
          return (
            acc +
            (exploration.sg_certificados
              ? exploration.sg_certificados.filter(
                  (certificado) => certificado.estado === 'CONCLUIDO'
                ).length
              : 0)
          );
        },
        0
      );
      setTotalConcludedCertifications(concludedCertificationsCount);

      const culturesCount = explorationsWithTitles.reduce(
        (acc, exploration) => {
          return acc + (exploration.culturas ? exploration.culturas.length : 0);
        },
        0
      );
      setTotalCultures(culturesCount);
    };

    loadExplorations();
  }, []);

  // Refresh data when component becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const storedExplorations =
          JSON.parse(sessionStorage.getItem('exploracoes')) || [];
        setExplorations(mapExplorationsWithTitles(storedExplorations));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Renderizar o gráfico de forma robusta e síncrona
  useEffect(() => {
    const buildRadarData = async () => {
      const colors = [
        '#27ae60',
        '#f1c40f',
        '#e74c3c',
        '#34495e',
        '#9b59b6',
        '#3498db',
      ];
      let colorIndex = 0;
      const datasets = [];
      const explorations =
        JSON.parse(sessionStorage.getItem('exploracoes')) || [];
      for (const exploration of explorations) {
        if (
          exploration.sg_certificados &&
          exploration.sg_certificados.length > 0
        ) {
          for (const certificado of exploration.sg_certificados) {
            if (
              (certificado.estado === 'CONCLUIDO' ||
                certificado.estado === 'VALIDACAO') &&
              certificado.detalhes_avaliacao
            ) {
              let cultura = exploration.culturas?.find(
                (cultura) => cultura.id === certificado.cultura_id
              );
              if (!cultura) continue;
              const culturaLabel = `${cultura.nome || 'Cultura'} - ${
                cultura.cultura || ''
              }`;
              const assignedColor = colors[colorIndex % colors.length];
              colorIndex++;
              try {
                const scores = await getPilarBestScores(
                  cultura.indicador_adequado
                );
                let avaliacao;
                if (typeof certificado.detalhes_avaliacao === 'string') {
                  try {
                    const parsedDetails = JSON.parse(
                      certificado.detalhes_avaliacao
                    );
                    avaliacao = parsedDetails.pilar_scores || parsedDetails;
                  } catch (e) {
                    console.error('Error parsing detalhes_avaliacao:', e);
                    avaliacao = {
                      ambiental: 0,
                      social: 0,
                      economico: 0,
                      governanca: 0,
                    };
                  }
                } else {
                  avaliacao = certificado.detalhes_avaliacao.pilar_scores ||
                    certificado.detalhes_avaliacao || {
                      ambiental: 0,
                      social: 0,
                      economico: 0,
                      governanca: 0,
                    };
                }
                const normalizeScore = (score, maxScore) => {
                  const ratio = maxScore > 0 ? score / maxScore : 0;
                  return Math.min(4, ratio * 4);
                };
                let final_avaliacao = {
                  ambiental: normalizeScore(
                    avaliacao.ambiental,
                    scores?.ambiental?.total_score || 100
                  ),
                  social: normalizeScore(
                    avaliacao.social,
                    scores?.social?.total_score || 100
                  ),
                  economico: normalizeScore(
                    avaliacao.economico,
                    scores?.económico?.total_score || 100
                  ),
                  governanca: normalizeScore(
                    avaliacao.governanca,
                    scores?.governança?.total_score || 100
                  ),
                };
                datasets.push({
                  label: culturaLabel,
                  data: [
                    final_avaliacao.ambiental || 0,
                    final_avaliacao.social || 0,
                    final_avaliacao.economico || 0,
                    final_avaliacao.governanca || 0,
                  ],
                  backgroundColor: 'transparent',
                  borderColor: assignedColor,
                  pointBackgroundColor: assignedColor,
                  pointBorderColor: assignedColor,
                  pointHoverBackgroundColor: '#fff',
                  pointHoverBorderColor: assignedColor,
                });
              } catch (err) {
                console.error('Error building radar dataset:', err);
                // skip this dataset
              }
            }
          }
        }
      }
      setRadarData({
        labels: ['Ambiental', 'Social', 'Económico', 'Governança'],
        datasets,
      });
    };
    buildRadarData();
    // console.log('Radar data built:');
  }, []);

  const radarOptions = {
    responsive: true,
    aspectRatio: 1.5,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'center',
        fullSize: true,
        reverse: false,
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          color: '#333',
          font: { size: 14, family: 'inherit' },
          padding: 10,
          usePointStyle: true,
          pointStyle: 'circle',
        },
        title: {
          display: false,
          text: '',
          color: '#666',
          font: { size: 12 },
        },
      },
    },
    layout: {
      padding: 0,
    },
    scales: {
      r: {
        angleLines: { color: '#ccc' },
        grid: { color: '#eee' },
        pointLabels: { color: '#222', font: { size: 14 } },
        min: 0,
        max: 4,
        ticks: {
          color: '#222',
          stepSize: 1,
          backdropColor: 'transparent',
        },
      },
    },
  };

  // Add this before your return statement
  const cellValues = [
    ['Ambiental', 'A', 'B', 'C', 'D'],
    ['Social', 'A', 'B', 'C', 'D'],
    ['Económico', 'A', 'B', 'C', 'D'],
    ['Governança', 'A', 'B', 'C', 'D'],
  ];

  /*   const cellColors = [
    ['#fff', 'lime', '#e5e5e5', '#e5e5e5', '#e5e5e5'],
    ['#fff', '#e5e5e5', 'green', '#e5e5e5', '#e5e5e5'],
    ['#fff', '#e5e5e5', '#e5e5e5', 'yellow', '#e5e5e5'],
    ['#fff', '#e5e5e5', '#e5e5e5', '#e5e5e5', 'red'],
  ]; */
  const cellColors = { A: 'lime', B: 'green', C: 'yellow', D: 'red' };

  // Refresh data when window gains focus (user returns to Dashboard)
  useEffect(() => {
    const handleFocus = () => {
      const storedExplorations =
        JSON.parse(sessionStorage.getItem('exploracoes')) || [];
      setExplorations(mapExplorationsWithTitles(storedExplorations)); // Always map titles
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  return (
    <section className="dashboard-container">
      <h3>Dashboard de Certificações Agrícolas</h3>

      <section className="stats">
        <div className="card">
          <h2>Explorações Activas</h2>
          <p>{explorations.length}</p>
        </div>
        <div className="card">
          <h2>Culturas Activas</h2>
          <p>{totalCultures}</p>
        </div>
        <div className="card">
          <h2>Culturas Pendentes</h2>
          <p>{totalPendingCertifications}</p>
        </div>
        <div className="card">
          <h2>Culturas Submetidas</h2>
          <p>{totalSubmittedCertifications}</p>
        </div>
        <div className="card">
          <h2>Culturas Concluídas</h2>
          <p>{totalConcludedCertifications}</p>
        </div>
      </section>

      <section className="card-container">
        <div className="map-container">
          <MapContainer
            center={center}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {explorations.map((exploration, idx) => {
              // Check if coordinates exist and parse them
              if (exploration.coordenadas) {
                try {
                  const [lat, lng] = exploration.coordenadas
                    .split(',')
                    .map((coord) => parseFloat(coord.trim()));
                  if (!isNaN(lat) && !isNaN(lng)) {
                    return (
                      <Marker key={idx} position={[lat, lng]} icon={customIcon}>
                        <Popup>
                          {exploration.title || `Exploração ${idx + 1}`}
                          <br />
                          Culturas: {exploration.culturas?.length || 0}
                        </Popup>
                      </Marker>
                    );
                  }
                } catch (error) {
                  console.error(
                    `Error parsing coordinates for exploration ${idx}:`,
                    error
                  );
                }
              }
              return null;
            })}
          </MapContainer>
        </div>
        <div className="chart-container">
          <Radar data={radarData} options={radarOptions} />
        </div>
      </section>

      <section>
        {explorations.map((exploration, index) => (
          <div key={index}>
            <div
              className="dropdown-bar"
              onClick={() =>
                setDropdownOpen((prev) => ({
                  ...prev,
                  [index]: !prev[index],
                }))
              }
            >
              <span>
                Exploração - {exploration.title || `Exploração ${index + 1}`}
              </span>
              <span
                style={{
                  transition: 'transform 0.2s',
                  display: 'inline-block',
                  transform: dropdownOpen[index]
                    ? 'rotate(90deg)'
                    : 'rot</ul>ate(0deg)',
                }}
              >
                ▶
              </span>
            </div>
            {dropdownOpen[index] && (
              <div className="dropdown-container">
                <div className="dropdown-item-left">
                  {exploration.culturas && exploration.culturas.length > 0 ? (
                    exploration.culturas.map((cultura, culturaIndex) => (
                      <div key={culturaIndex} className="dropdown-item-bar">
                        {cultura.nome || `Cultura ${culturaIndex + 1}`} -{' '}
                        {cultura.cultura || 'N/A'}
                      </div>
                    ))
                  ) : (
                    <div className="dropdown-item-bar">
                      Nenhuma cultura encontrada
                    </div>
                  )}
                </div>

                <div className="dropdown-item-right">
                  {exploration.culturas && exploration.culturas.length > 0 ? (
                    exploration.culturas.map((cultura, culturaIndex) => {
                      // Find the corresponding certificate for this cultura
                      const certificado = exploration.sg_certificados?.find(
                        (cert) => cert.cultura_id === cultura.id
                      );

                      // If no certificate exists, create an empty element with gray background
                      if (!certificado?.estado)
                        return (
                          <div
                            key={culturaIndex}
                            className="dropdown-item-bar-empty"
                          >
                            <div>Sem certificado associado</div>
                          </div>
                        );

                      const estado = certificado.estado;
                      const certificado_id = (() => {
                        // Find the corresponding certificate for this cultura
                        const certificado = exploration.sg_certificados?.find(
                          (cert) => cert.cultura_id === cultura.id
                        );
                        // Return the certificate ID if found, otherwise default to null
                        return certificado?.id || null;
                      })();
                      let className = 'dropdown-item-bar-';

                      if (estado === 'CRIADO' || estado === 'PENDENTE') {
                        className += 'pendente';
                      } else if (
                        estado === 'PROCESSADO' ||
                        estado === 'VALIDACAO' ||
                        estado === 'SUBMETIDA'
                      ) {
                        className += 'submetida';
                      } else if (
                        estado === 'CONCLUIDO' ||
                        estado === 'CONCLUIDA'
                      ) {
                        className += 'concluida';
                      } else {
                        className += 'pendente'; // default
                      }

                      return (
                        <div key={culturaIndex} className={className}>
                          <div>Estado: {estado}</div>
                          <div className="dropdown-item-bar-btns">
                            {(estado === 'CRIADO' || estado === 'PENDENTE') && (
                              <>
                                <button
                                  onClick={() => {
                                    // Navigate to Pilares page with certification data
                                    window.location.href = `/pilares?certification_id=${certificado_id}&exploration_id=${exploration.id}`;
                                  }}
                                  className="btn-dash-edit"
                                >
                                  Editar
                                </button>
                                <button
                                  onClick={() =>
                                    handleGeneratePdf(certificado_id)
                                  }
                                  className="btn-dash-pdf"
                                >
                                  PDF
                                </button>
                              </>
                            )}
                            {(estado === 'PROCESSADO' ||
                              estado === 'SUBMETIDA') && (
                              <button
                                onClick={() =>
                                  handleGeneratePdf(certificado_id)
                                }
                                className="btn-dash-pdf"
                              >
                                PDF
                              </button>
                            )}
                            {(estado === 'CONCLUIDO' ||
                              estado === 'CONCLUIDA' ||
                              estado === 'VALIDACAO') && (
                              <>
                                <button
                                  className="btn-dash-view"
                                  onClick={() =>
                                    setConcluidaDropdownOpen((prev) => ({
                                      ...prev,
                                      [`${index}-${culturaIndex}`]:
                                        !prev[`${index}-${culturaIndex}`],
                                    }))
                                  }
                                >
                                  Visualizar
                                </button>
                                <button
                                  onClick={() =>
                                    handleGeneratePdf(certificado_id)
                                  }
                                  className="btn-dash-pdf"
                                >
                                  PDF
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="dropdown-item-bar-empty">
                      <div>Sem certificados associados</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Luís -> Card com a info da cultura conluída */}
            {exploration.culturas &&
              exploration.culturas.map((cultura, culturaIndex) => {
                const certificado = exploration.sg_certificados?.find(
                  (cert) => cert.cultura_id === cultura.id
                );
                const estado = certificado?.estado;
                const isCompleted =
                  estado === 'CONCLUIDO' ||
                  estado === 'CONCLUIDA' ||
                  estado === 'VALIDACAO';
                const detailsKey = `${index}-${culturaIndex}`;

                return (
                  isCompleted &&
                  dropdownOpen[index] &&
                  concluidaDropdownOpen[detailsKey] && (
                    <ConcludedCultureCard
                      key={detailsKey}
                      cultura={cultura}
                      certificado={certificado}
                      culturaIndex={culturaIndex}
                      cellValues={cellValues}
                      cellColors={cellColors}
                    />
                  )
                );
              })}
          </div>
        ))}
      </section>
    </section>
  );
};

export default Dashboard;
